import React, { useEffect, useRef } from 'react';
import JsBarcode from "jsbarcode";
import styles from '../css/CertDocPrescription.module.css';

export default function CertDocPrescription({
  title = 'CERTIFIED COPY OF DOCTORS PRESCRIPTION',
  lines = [],
  pharmacistSignature = null,
  copyCost = 'R0.25',
  total = 'R148.56',
  pharmacyName = 'Durban North Pharmacy',
  pharmacyRegistration = '0602809',
  receiptNumber = null,
}) {
  const barcodeRef = useRef(null);

  // Generate barcode when receiptNumber is available
  useEffect(() => {
    if (receiptNumber && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, receiptNumber, {
          format: "CODE128",
          displayValue: false,
          width: 2,
          height: 80,
          margin: 8,
          background: "transparent",
          lineColor: "#000000",
        });
      } catch (error) {
        console.error("Error generating barcode in CertDocPrescription:", error);
      }
    }
  }, [receiptNumber]);
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>

      <div className={styles.body}>
        {lines.map((line, idx) => {
          const instructionLine = Array.isArray(line.instructions)
            ? line.instructions.join('   ')
            : (line.instructions || '');
          return (
            <div key={idx} className={styles.drugBlock}>
              <div className={styles.drugHeader}>
                <span className={styles.colCode}>{line.code}</span>
                <span className={styles.colDash}>-</span>
                <span className={styles.colDrug}>{line.drug}</span>
                <span className={styles.colQty}>{line.qty}</span>
                <span className={styles.colOd}>OD</span>
                <span className={styles.colIcd}>ICD10: {line.icd10}</span>
                <span className={styles.colPrice}>{line.price}</span>
              </div>
              {instructionLine && (
                <div className={styles.instruction}>{instructionLine}</div>
              )}
            </div>
          );
        })}

        {/* Tracer label */}
        <div className={styles.tracerCard}>
          <div className={styles.tracerHeader}>{pharmacyName}</div>
          <div className={styles.tracerAddress}>
            {/* Using registration as secondary identifier; physical address/phone can be wired later if available */}
            Reg: {pharmacyRegistration}
          </div>
          <div className={styles.divider} />
          {/* Summary header row – mirrors script/tracer info conceptually */}
          <div className={styles.tracerRxLine}>
            <span>
              Rx: <strong>{lines.length > 0 ? lines[0].code || '' : ''}</strong>
            </span>
            <span>{lines.length > 0 ? lines[0].drug || '' : ''}</span>
            <span>
              Qty: {lines.reduce((sum, l) => sum + (parseFloat(l.qty) || 0), 0)}
            </span>
          </div>
          {/* One line per medication, similar to how the tracer label configuration iterates medications */}
          {lines.map((line, idx) => {
            const num = String(idx + 1).padStart(2, '0');
            return (
              <React.Fragment key={`tracer-${idx}`}>
                <div className={styles.tracerLine}>
                  {num}: {line.drug}
                  {line.icd10 ? ` ${line.icd10}` : ''}
                </div>
                <div className={styles.tracerLine}>Qty: {line.qty}</div>
              </React.Fragment>
            );
          })}
          <div className={styles.divider} />
          <div className={styles.tracerCopyLine}>
            <span>+COPY {copyCost}:</span>
            <span className={styles.valueStrong}>{total}</span>
          </div>
          <div className={styles.tracerTitle}>TRAILER LABEL</div>
          <svg ref={barcodeRef} className={styles.barcode} role="img" aria-label={`Barcode for receipt ${receiptNumber || 'N/A'}`} />
        </div>

        <div className={styles.footerRow}>
          <div className={styles.signatureBlock}>
            {pharmacistSignature ? (
              <img className={styles.sigImage} src={pharmacistSignature} alt="Pharmacist signature" />
            ) : (
              <div className={styles.sigPlaceholder}>Pharmacist Signature</div>
            )}
            <div className={styles.sigCaption}>Pharmacist</div>
            <div className={styles.pharmacyInfo}>
              <div className={styles.row}>
                <span className={styles.label}>Pharmacy:</span>
                <span className={styles.value}>{pharmacyName}</span>
              </div>
              <div className={styles.row}>
                <span className={styles.label}>Registration Number:</span>
                <span className={styles.value}>{pharmacyRegistration}</span>
              </div>
            </div>
          </div>
          <div className={styles.totalsCompact}>
            <div><span className={styles.label}>Copy:</span> <span className={styles.value}>{copyCost}</span></div>
            <div><span className={styles.label}>Total excl. VAT:</span> <span className={styles.value}>{total}</span></div>
          </div>
        </div>
      </div>
    </section>
  );
}

